import {
	Card,
	NumberInput,
	Select,
	SimpleGrid,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import type {
	AssistantSettingsDto,
	KioskCandidateDto,
} from "@/types/ai-assistant-admin";
import { LIMIT_RULES, type LimitKey } from "./ai-assistant.logic";
import {
	BELOW_INPUT_ORDER,
	LIMIT_GRID_COLS,
	LIMIT_GROUPS,
} from "./limit-groups";
import { RetentionRulesNote } from "./retention-rules-note";
import { VOICE_LIMIT_RULES, type VoiceNumberKey } from "./voice-settings.logic";
import { WallNocFields } from "./wall-noc-fields";
import { WallVoiceModeField } from "./wall-voice-mode-field";

interface Props {
	form: AssistantSettingsDto;
	errors: Partial<Record<string, string>>;
	kioskCandidates: KioskCandidateDto[];
	/** Batas transkrip dari server; dipakai grup "Riwayat" untuk menjelaskan dua aturan retensi. */
	transcriptRetentionDays: number;
	disabled: boolean;
	onChange: <K extends keyof AssistantSettingsDto>(
		key: K,
		value: AssistantSettingsDto[K],
	) => void;
}

/** Kartu "Batas pemakaian": input dikelompokkan, keterangan di bawah input agar kotak sejajar. */
export function LimitsSection({
	form,
	errors,
	kioskCandidates,
	transcriptRetentionDays,
	disabled,
	onChange,
}: Props) {
	const limitInput = (key: LimitKey | VoiceNumberKey) => {
		const rule =
			key in VOICE_LIMIT_RULES
				? VOICE_LIMIT_RULES[key as VoiceNumberKey]
				: LIMIT_RULES[key as LimitKey];
		const description = [rule.zeroUnlimited && "0 = tanpa batas", rule.hint]
			.filter(Boolean)
			.join(". ");
		return (
			<NumberInput
				key={key}
				label={rule.label}
				description={description || undefined}
				inputWrapperOrder={[...BELOW_INPUT_ORDER]}
				min={rule.min}
				max={rule.max}
				allowDecimal={false}
				thousandSeparator="."
				decimalSeparator=","
				value={form[key]}
				onChange={(v) => onChange(key, typeof v === "number" ? v : Number.NaN)}
				error={errors[key]}
				disabled={disabled}
			/>
		);
	};

	const kioskSelect = (
		<Select
			key="kioskUserId"
			label="Akun kiosk /wall"
			description="Akun bersama di TV NOC; memakai kuota kiosk."
			inputWrapperOrder={[...BELOW_INPUT_ORDER]}
			placeholder="Belum dipilih"
			clearable
			searchable
			data={kioskCandidates.map((c) => ({ value: c.id, label: c.name }))}
			value={form.kioskUserId}
			onChange={(v) => onChange("kioskUserId", v)}
			disabled={disabled}
		/>
	);

	return (
		<Card withBorder radius="md" p="lg" data-limits-section>
			<Title order={4} mb={4}>
				Batas pemakaian
			</Title>
			<Text size="sm" c="dimmed" mb="md">
				Kuota harian direset pukul 00:00 WITA.
			</Text>
			<Stack gap="lg">
				{LIMIT_GROUPS.map((group) => (
					<Stack key={group.id} gap="xs" data-limit-group={group.id}>
						<Text size="xs" fw={700} tt="uppercase" c="dimmed">
							{group.title}
						</Text>
						<SimpleGrid cols={LIMIT_GRID_COLS} verticalSpacing="md">
							{group.fields.map((field) =>
								field === "kioskUserId" ? kioskSelect : limitInput(field),
							)}
						</SimpleGrid>
						{group.id === "history" ? (
							<RetentionRulesNote
								transcriptRetentionDays={transcriptRetentionDays}
								inactiveRetentionDays={form.retentionDays}
							/>
						) : null}
						{group.id === "kiosk" ? (
							<WallVoiceModeField
								value={form.wallVoiceMode}
								error={errors.wallVoiceMode}
								disabled={disabled}
								onChange={(mode) => onChange("wallVoiceMode", mode)}
							/>
						) : null}
						{group.id === "kiosk" && form.wallVoiceMode === "noc" ? (
							<WallNocFields
								form={form}
								errors={errors}
								disabled={disabled}
								onChange={onChange}
							/>
						) : null}
					</Stack>
				))}
			</Stack>
		</Card>
	);
}
